import type { MessageKey, TranslationParams } from '../i18n';
import type { NormalizedAnalysis, PreviewBackground, UiRecord, ZoomMode } from './types';
import { clamp } from './utils';

export interface PreviewState {
  background: PreviewBackground;
  zoomMode: ZoomMode;
  customScale: number;
  showBoundingBox: boolean;
  showMarginGuides: boolean;
}

export interface ImagePreviewOptions {
  translate: (key: MessageKey, params?: TranslationParams) => string;
  onBackground: (background: PreviewBackground) => void;
  onZoomMode: (mode: ZoomMode) => void;
  onZoom: (scale: number) => void;
  onBoundingBox: (visible: boolean) => void;
  onMarginGuides: (visible: boolean) => void;
}

const backgrounds: PreviewBackground[] = [
  'white',
  'black',
  'gray',
  'red',
  'green',
  'blue',
  'checker',
];

const backgroundKeys: Record<PreviewBackground, MessageKey> = {
  white: 'backgroundWhite',
  black: 'backgroundBlack',
  gray: 'backgroundGray',
  red: 'backgroundRed',
  green: 'backgroundGreen',
  blue: 'backgroundBlue',
  checker: 'backgroundChecker',
};

export function renderImagePreview(
  record: UiRecord,
  analysis: NormalizedAnalysis,
  state: PreviewState,
  options: ImagePreviewOptions,
): string {
  const t = options.translate;
  const box = analysis.boundingBox;
  const zoomLabel =
    state.zoomMode === 'fit'
      ? t('fit')
      : `${Math.round((state.zoomMode === 'actual' ? 1 : state.customScale) * 100)}%`;
  return `
    <div class="preview-shell" data-testid="image-preview">
      <div class="preview-toolbar">
        <div class="segmented-control" aria-label="${t('previewScale')}">
          <button class="segmented-control__item${state.zoomMode === 'fit' ? ' is-selected' : ''}" type="button" data-preview-action="fit" aria-pressed="${String(state.zoomMode === 'fit')}" data-testid="preview-fit">${t('fit')}</button>
          <button class="segmented-control__item${state.zoomMode === 'actual' ? ' is-selected' : ''}" type="button" data-preview-action="actual" aria-pressed="${String(state.zoomMode === 'actual')}" data-testid="preview-actual">${t('actualSize')}</button>
        </div>
        <div class="zoom-control" aria-label="${t('zoomControls')}">
          <button class="icon-button" type="button" data-preview-action="zoom-out" aria-label="${t('zoomOut')}" data-testid="zoom-out">−</button>
          <output class="zoom-output mono-value" data-testid="zoom-value">${zoomLabel}</output>
          <button class="icon-button" type="button" data-preview-action="zoom-in" aria-label="${t('zoomIn')}" data-testid="zoom-in">＋</button>
        </div>
        <div class="preview-guides">
          <label class="checkbox-control checkbox-control--compact" title="${box ? '' : t('bboxUnavailable')}">
            <input type="checkbox" data-preview-action="bbox" ${state.showBoundingBox ? 'checked' : ''} ${box ? '' : 'disabled'} data-testid="toggle-bbox" />
            <span>${t('showBoundingBox')}</span>
          </label>
          <label class="checkbox-control checkbox-control--compact" title="${box ? '' : t('bboxUnavailable')}">
            <input type="checkbox" data-preview-action="margins" ${state.showMarginGuides ? 'checked' : ''} ${box ? '' : 'disabled'} data-testid="toggle-margins" />
            <span>${t('showMarginGuides')}</span>
          </label>
        </div>
      </div>

      <div class="background-picker" role="group" aria-label="${t('background')}">
        <span class="background-picker__label">${t('background')}</span>
        ${backgrounds
          .map(
            (background) => `
              <button class="background-button${state.background === background ? ' is-selected' : ''}" type="button" data-preview-background="${background}" aria-label="${t(backgroundKeys[background])}" aria-pressed="${String(state.background === background)}" data-testid="preview-background-${background}">
                <span class="background-swatch background-swatch--${background}" aria-hidden="true"></span>
                <span>${t(backgroundKeys[background])}</span>
              </button>
            `,
          )
          .join('')}
      </div>

      <div class="preview-viewport preview-background--${state.background}" data-preview-viewport data-testid="preview-stage">
        <div
          class="preview-artboard"
          data-preview-artboard
          data-width="${analysis.width}"
          data-height="${analysis.height}"
          data-mode="${state.zoomMode}"
          data-custom-scale="${state.customScale}"
        >
          <img src="${escapeAttribute(record.objectUrl)}" alt="${t('previewAlt', { name: escapeAttribute(record.file.name) })}" draggable="false" data-preview-image data-testid="preview-image" />
          ${renderGuides(analysis, state)}
        </div>
      </div>
    </div>
  `;
}

function renderGuides(analysis: NormalizedAnalysis, state: PreviewState): string {
  const box = analysis.boundingBox;
  if (!box || analysis.width <= 0 || analysis.height <= 0) return '';
  const left = (box.x / analysis.width) * 100;
  const top = (box.y / analysis.height) * 100;
  const width = (box.width / analysis.width) * 100;
  const height = (box.height / analysis.height) * 100;
  return `
    <div class="bbox-guide${state.showBoundingBox ? '' : ' is-hidden'}" style="left:${left}%;top:${top}%;width:${width}%;height:${height}%" aria-hidden="true" data-testid="bbox-guide"></div>
    <div class="margin-guides${state.showMarginGuides ? '' : ' is-hidden'}" aria-hidden="true" data-testid="margin-guides">
      <span class="margin-guide margin-guide--vertical" style="left:${left}%"></span>
      <span class="margin-guide margin-guide--vertical" style="left:${left + width}%"></span>
      <span class="margin-guide margin-guide--horizontal" style="top:${top}%"></span>
      <span class="margin-guide margin-guide--horizontal" style="top:${top + height}%"></span>
    </div>
  `;
}

export function bindImagePreview(
  container: HTMLElement,
  state: PreviewState,
  options: ImagePreviewOptions,
): () => void {
  const viewport = container.querySelector<HTMLElement>('[data-preview-viewport]');
  const artboard = container.querySelector<HTMLElement>('[data-preview-artboard]');
  const image = container.querySelector<HTMLImageElement>('[data-preview-image]');
  if (!viewport || !artboard || !image) return () => undefined;

  container.querySelectorAll<HTMLButtonElement>('[data-preview-background]').forEach((button) => {
    button.addEventListener('click', () =>
      options.onBackground(button.dataset.previewBackground as PreviewBackground),
    );
  });
  container
    .querySelector<HTMLButtonElement>('[data-preview-action="fit"]')
    ?.addEventListener('click', () => options.onZoomMode('fit'));
  container
    .querySelector<HTMLButtonElement>('[data-preview-action="actual"]')
    ?.addEventListener('click', () => options.onZoomMode('actual'));
  container
    .querySelector<HTMLButtonElement>('[data-preview-action="zoom-out"]')
    ?.addEventListener('click', () => {
      options.onZoom(clamp(readAppliedScale(artboard) / 1.25, 0.02, 8));
    });
  container
    .querySelector<HTMLButtonElement>('[data-preview-action="zoom-in"]')
    ?.addEventListener('click', () => {
      options.onZoom(clamp(readAppliedScale(artboard) * 1.25, 0.02, 8));
    });
  container
    .querySelector<HTMLInputElement>('[data-preview-action="bbox"]')
    ?.addEventListener('change', (event) => {
      options.onBoundingBox((event.currentTarget as HTMLInputElement).checked);
    });
  container
    .querySelector<HTMLInputElement>('[data-preview-action="margins"]')
    ?.addEventListener('change', (event) => {
      options.onMarginGuides((event.currentTarget as HTMLInputElement).checked);
    });

  const applySize = (): void => sizeArtboard(viewport, artboard, image, state);
  if (image.complete) applySize();
  else image.addEventListener('load', applySize, { once: true });
  const observer = new ResizeObserver(applySize);
  observer.observe(viewport);
  return () => observer.disconnect();
}

function sizeArtboard(
  viewport: HTMLElement,
  artboard: HTMLElement,
  image: HTMLImageElement,
  state: PreviewState,
): void {
  const width = Number(artboard.dataset.width) || image.naturalWidth;
  const height = Number(artboard.dataset.height) || image.naturalHeight;
  if (width <= 0 || height <= 0) return;
  const fitScale = clamp(
    Math.min((viewport.clientWidth - 48) / width, (viewport.clientHeight - 48) / height),
    0.02,
    8,
  );
  const scale =
    state.zoomMode === 'fit' ? fitScale : state.zoomMode === 'actual' ? 1 : state.customScale;
  artboard.style.width = `${Math.max(1, width * scale)}px`;
  artboard.style.height = `${Math.max(1, height * scale)}px`;
  artboard.dataset.appliedScale = String(scale);
  const output = viewport.parentElement?.querySelector<HTMLOutputElement>('.zoom-output');
  if (output)
    output.textContent =
      state.zoomMode === 'fit' ? output.textContent : `${Math.round(scale * 100)}%`;
}

function readAppliedScale(artboard: HTMLElement): number {
  const scale = Number(artboard.dataset.appliedScale);
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

function escapeAttribute(value: string): string {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML.replaceAll('`', '&#96;');
}
